import React from 'react';
import _ from 'underscore';

import {MakeFieldsOptional} from 'util/Types';

import * as ModalContainer from './ModalContainer';

interface ModalProps<T> {
  onRequestSubmit?(result: T): void | Promise<void>;
  onRequestClose(): void;
  modalKey?: string;
}

export type ModalReturnType<P> = P extends ModalProps<infer T> ? T : void;

/**
 * Open a modal and return a promise that resolves with the modal's "return value" when the modal
 * finishes.
 *
 * The modal is assumed to have a onRequestSubmit and onRequestClose prop that, when called, should
 * close the modal. If onRequestSubmit is called, its argument is used as the resolved value of the
 * promise. If onRequestClose is called, null is used as the return value of the promise.
 *
 * An optional modalKey value can be passed in to allow for closing the modal outside of onRequestSubmit/onRequestClose.
 */
export default function runModal<P extends ModalProps<unknown>>(
  ModalComponent: React.ComponentType<P>,
  props: MakeFieldsOptional<P, 'onRequestSubmit' | 'onRequestClose' | 'modalKey'>
): Promise<ModalReturnType<P> | null> {
  return new Promise((resolve) => {
    const modalKey = props.modalKey ?? _.uniqueId('run-modal');
    ModalContainer.open(
      modalKey,
      <ModalComponent
        // TS isn't smart enough to figure out that excluding and re-adding onRequestSubmit and
        // onRequestClose works, so do an unsafe cast to P to work around.
        {...(props as P)}
        onRequestSubmit={async (result: ModalReturnType<P>) => {
          if (props.onRequestSubmit) {
            await props.onRequestSubmit(result);
          }
          ModalContainer.close(modalKey);
          resolve(result);
        }}
        onRequestClose={() => {
          if (props.onRequestClose) {
            props.onRequestClose();
          }
          ModalContainer.close(modalKey);
          resolve(null);
        }}
      />
    );
  });
}
